<script setup lang="ts" generic="ListItem">
import type Menu from 'primevue/menu';
import type { MenuPassThroughMethodOptions, MenuPassThroughOptions } from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, ref } from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import { QA_TABLES, TRANSLATION_GROUP_TABLE } from '@modules/tables/constants';

import type {
  ITableMultiselectActionItem,
  ITableMultiselectActionsEmits,
  ITableMultiselectActionsProps,
} from './types';

const props = defineProps<ITableMultiselectActionsProps<ListItem>>();
const emit = defineEmits<ITableMultiselectActionsEmits<ListItem>>();

const menu = ref<InstanceType<typeof Menu> | null>(null);

const menuPt: MenuPassThroughOptions = {
  list: {
    class: [
      'max-h-[300px]',
      'overflow-y-auto',
    ],
    role: '',
  },
  root: { role: '' },
  item: ({ context }: MenuPassThroughMethodOptions) => ({
    'role': '',
    'data-test-el': QA_TABLES.headActionItem,
    'data-test-attr-item-index': context.index,
  }),
};

const options = computed<ITableMultiselectActionItem[]>(() => (
  Object.keys(props.options).map((id) => ({
    ...props.options[id],
    id,
  }))
));

function toggle(event: Event): void {
  if (props.disabled) {
    return;
  }
  menu.value?.toggle(event);
}

function handleActionClick(item: MenuItem): void {
  emit('select', item.id, props.listItem);
}
</script>

<template>
  <div v-data-qa-auto-id="'VTableMultiselectActions'" class="flex w-[35px] items-center justify-center">
    <VIconButton
      v-data-test="{ el: QA_TABLES.headActionsButton }"
      :disabled="disabled"
      icon="pi pi-ellipsis-v"
      class="!h-8 !w-8 !p-0"
      :class="{ 'text-muted-color': disabled }"
      @click="toggle"
    />
    <PMenu
      ref="menu"
      :model="options"
      popup
      :pt="menuPt"
    >
      <template #item="{ item }">
        <span
          class="p-menu-item-link"
          @click="handleActionClick(item)"
        >
          <i
            v-if="item.icon"
            class="pi mr-2 text-lg text-primary"
            :class="item.icon"
          />
          <span class="p-menu-item-text">{{ item.label }}</span>
        </span>
      </template>
      <template
        v-if="options.length === 0"
        #end
      >
        <span class="p-menu-item-link pointer-events-none p-3 text-disabled-color">
          {{ $getTranslation(TRANSLATION_GROUP_TABLE, 'NO_AVAILABLE_ACTIONS') }}
        </span>
      </template>
    </PMenu>
  </div>
</template>
